import { useState } from 'react';
import { adminApi } from '@/services/admin';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/Button';

const downloadBlob = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const getDateRange = (preset: string): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date();

  switch (preset) {
    case 'this_month':
      start.setDate(1);
      break;
    case 'last_month':
      start.setMonth(start.getMonth() - 1, 1);
      end.setDate(0); // last day of previous month
      break;
    case 'last_90':
      start.setDate(start.getDate() - 90);
      break;
    default: // last_30
      start.setDate(start.getDate() - 30);
  }

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const PRESETS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_90', label: 'Last 90 Days' },
];

export const AdminExportsPage = () => {
  const defaults = getDateRange('last_30');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [activePreset, setActivePreset] = useState('last_30');
  const [loading, setLoading] = useState<string | null>(null);
  const toast = useToast();

  const applyPreset = (preset: string) => {
    const range = getDateRange(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setActivePreset(preset);
  };

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setActivePreset(''); // clear preset when manually editing
  };

  const handleExportOrders = async () => {
    setLoading('orders');
    try {
      const csv = await adminApi.exportOrdersCsv(startDate, endDate);
      downloadBlob(csv, `orders-${startDate}-to-${endDate}.csv`, 'text/csv');
      toast.success('Orders CSV downloaded');
    } catch {
      toast.error('Failed to export orders');
    } finally {
      setLoading(null);
    }
  };

  const handleExportRefunds = async () => {
    setLoading('refunds');
    try {
      const csv = await adminApi.exportRefundsCsv(startDate, endDate);
      downloadBlob(csv, `refunds-${startDate}-to-${endDate}.csv`, 'text/csv');
      toast.success('Refunds CSV downloaded');
    } catch {
      toast.error('Failed to export refunds');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div>
      <h1
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-6)',
        }}
      >
        Financial Exports
      </h1>

      <div style={{ maxWidth: '520px' }}>
        {/* Date range card */}
        <div
          style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            padding: 'var(--space-5)',
            marginBottom: 'var(--space-4)',
          }}
        >
          {/* Presets */}
          <div className="flex" style={{ gap: 'var(--space-1)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
            {PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => applyPreset(p.value)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-xs)',
                  minHeight: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: activePreset === p.value ? 'var(--font-weight-semibold)' : 'var(--font-weight-regular)',
                  color: activePreset === p.value ? 'var(--color-primary-700)' : 'var(--text-secondary)',
                  backgroundColor: activePreset === p.value ? 'var(--color-primary-50)' : 'transparent',
                  border: `1px solid ${activePreset === p.value ? 'var(--color-primary-200)' : 'var(--border-default)'}`,
                  borderRadius: 'var(--radius-full)',
                  cursor: 'pointer',
                  transition: 'all var(--duration-fast) var(--ease-out)',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Date inputs */}
          <div className="flex items-end" style={{ gap: 'var(--space-3)' }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  marginBottom: '4px',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                From
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('start', e.target.value)}
                className="admin-input"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  marginBottom: '4px',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                To
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange('end', e.target.value)}
                className="admin-input"
              />
            </div>
          </div>
        </div>

        {/* Export buttons */}
        <div className="flex" style={{ gap: 'var(--space-3)' }}>
          <Button
            onClick={handleExportOrders}
            disabled={loading === 'orders'}
            style={{ flex: 1 }}
          >
            {loading === 'orders' ? 'Exporting...' : 'Orders CSV'}
          </Button>
          <Button
            onClick={handleExportRefunds}
            disabled={loading === 'refunds'}
            variant="secondary"
            style={{ flex: 1 }}
          >
            {loading === 'refunds' ? 'Exporting...' : 'Refunds CSV'}
          </Button>
        </div>
      </div>
    </div>
  );
};
